import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useHistory, useParams } from 'react-router-dom';
import { Button, LoadingSpinner, formatDatetime } from 'common.ui';
import { auditEventsClient } from 'api/AdgangClients';
import { AuditEventResponse, AuditEventSource, UserType } from 'api/adgang';
import Page from 'components/page/Page';
import RoutePaths from 'RoutePaths';
import { Col, Container, Row } from 'react-bootstrap';

interface IParams {
  id: string;
}

interface IValue {
  label: string;
  value?: string | null;
  to?: string;
}

function sourceLabel(source: AuditEventSource | string | undefined): string {
  if (source === AuditEventSource.DirectoryAudit) return 'Katalogendring';
  if (source === AuditEventSource.SignIn) return 'Innlogging';
  return source ?? '';
}

// Brukertypen slås opp i backend. Internbrukersiden slår opp på Entra-id (InternalUserId), mens
// eksternbrukersiden bruker B2C-id (targetId). Mål som ikke er en aktiv bruker hos oss får ingen lenke.
function targetUserRoute(auditEvent: AuditEventResponse): string | undefined {
  if (auditEvent.targetUserType === UserType.Internal && auditEvent.targetInternalUserId)
    return `${RoutePaths.internalusers}/${auditEvent.targetInternalUserId}`;
  if (auditEvent.targetUserType === UserType.External && auditEvent.targetId)
    return `${RoutePaths.externalusers}/${auditEvent.targetId}`;
  return undefined;
}

const InfoField = ({ label, value, to }: IValue) =>
  value ? (
    <Row className='info-field-row'>
      <Col>
        <b>{label}</b>
        <div>{to ? <Link to={to}>{value}</Link> : value}</div>
      </Col>
    </Row>
  ) : null;

function AuditLog() {
  const history = useHistory();
  const { id } = useParams<IParams>();

  const { data: auditEvent, isLoading } = useQuery({
    queryKey: ['auditEvent', id],
    queryFn: () => auditEventsClient.apiAuditEventsIdGet({ id: Number(id) })
  });

  if (isLoading) return <LoadingSpinner />;

  if (!auditEvent) {
    return (
      <Page header='Revisjonshendelse' title={['Revisjonslogg', 'Hendelse']}>
        <p>Fant ingen revisjonshendelse med id {id}.</p>
        <Button text='Tilbake' styleType='light' onClick={() => history.push(RoutePaths.auditlogs)} />
      </Page>
    );
  }

  return (
    <Page header='Revisjonshendelse' title={['Revisjonslogg', 'Hendelse', String(id)]}>
      <Container fluid>
        <Row>
          <Col sm={12} lg={8} xl={4} className='info-field-group'>
            <p>
              <b className='info-field-group--title'> Hendelse </b>
            </p>
            <InfoField label='Kilde' value={sourceLabel(auditEvent.source)} />
            <InfoField
              label='Tidspunkt'
              value={auditEvent.eventDateTime && formatDatetime(auditEvent.eventDateTime, true)}
            />
            <InfoField label='Kategori' value={auditEvent.category} />
            <InfoField label='Aktivitet' value={auditEvent.activity} />
            <InfoField label='Resultat' value={auditEvent.result} />
            <InfoField label='Resultatårsak' value={auditEvent.resultReason} />
          </Col>
          <Col sm={12} lg={8} xl={4} className='info-field-group'>
            <p>
              <b className='info-field-group--title'> Aktør og mål </b>
            </p>
            <InfoField label='Utført av' value={auditEvent.initiatedByDisplayName} />
            <InfoField label='Utført av (UPN)' value={auditEvent.initiatedByUpn} />
            <InfoField label='Utført av (id)' value={auditEvent.initiatedById} />
            <InfoField
              label='Mål'
              value={auditEvent.targetDisplayName}
              to={targetUserRoute(auditEvent)}
            />
            <InfoField label='Mål (UPN)' value={auditEvent.targetUpn} />
            <InfoField
              label='Mål (id)'
              value={auditEvent.targetId}
              to={targetUserRoute(auditEvent)}
            />
          </Col>
          <Col sm={12} lg={8} xl={4} className='info-field-group'>
            <p>
              <b className='info-field-group--title'> Kontekst </b>
            </p>
            <InfoField label='IP-adresse' value={auditEvent.ipAddress} />
            <InfoField label='Land/region' value={auditEvent.locationCountryOrRegion} />
            <InfoField label='Korrelasjons-id' value={auditEvent.correlationId} />
            <InfoField label='Graph hendelses-id' value={auditEvent.graphEventId} />
          </Col>
        </Row>
        {auditEvent.rawJson && (
          <Row>
            <Col sm={12} lg={8}>
              <p>
                <b className='info-field-group--title'> Rådata </b>
              </p>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '1em',
                  maxHeight: '30em',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}
              >
                {formatRawJson(auditEvent.rawJson)}
              </pre>
            </Col>
          </Row>
        )}
        <Row className='pt-3'>
          <Col sm={12}>
            <Button text='Tilbake' styleType='light' onClick={() => history.push(RoutePaths.auditlogs)} />
          </Col>
        </Row>
      </Container>
    </Page>
  );
}

// Forsøker å pretty-printe rådata; faller tilbake til rå streng om det ikke er gyldig JSON.
function formatRawJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

export default AuditLog;
